import { type TypedDocumentNode, gql } from '@apollo/client';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
} from 'date-fns';
import {
  FormattedMessage,
  FormattedNumber,
  IntlFormatters,
  useIntl,
} from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Clock } from '@sorare/core/src/atoms/icons/Clock';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import {
  BodyM,
  BodyS,
  Caption,
  HeadlineM,
  LabelL,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import Dialog from '@sorare/core/src/components/dialog';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { zeroMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { glossary } from '@sorare/core/src/lib/glossary';
import { monetaryAmountFragment } from '@sorare/core/src/lib/monetaryAmount';

import { useFootballEventOnce } from '@sorare/gameplay/src/lib/events';

import { firstMissionDescription } from 'lib/onboarding';

import type { OnboardingPromoRecapDialog_so5OnboardingPromotionalEvent } from './__generated__/index.graphql';
import banner from './assets/banner.png';
import marketCredit from './assets/marketCredit.png';

const now = new Date();
const formatTimeLeft = (
  endDate: Date,
  formatMessage: IntlFormatters['formatMessage']
) => {
  const daysLeft = differenceInDays(endDate, now);
  if (daysLeft >= 1)
    return formatMessage(
      {
        id: 'earlyAccess.formatTimeLeft.1dayOrMore',
        defaultMessage:
          '{daysLeft} {daysLeft, plural, one {day left} other {days left}}',
      },
      {
        daysLeft,
      }
    );

  const hoursLeft = differenceInHours(endDate, now);
  if (hoursLeft >= 1)
    return formatMessage(
      {
        id: 'earlyAccess.formatTimeLeft.1hourOrMore',
        defaultMessage:
          '{hoursLeft} {hoursLeft, plural, one {hour left} other {hours left}}',
      },
      {
        hoursLeft,
      }
    );
  const minutesLeft = differenceInMinutes(endDate, now);
  return formatMessage(
    {
      id: 'earlyAccess.formatTimeLeft.1minuteOrMore',
      defaultMessage:
        '{minutesLeft} {minutesLeft, plural, one {minute left} other {minutes left}}',
    },
    {
      minutesLeft,
    }
  );
};

const Root = styled(Vertical).attrs({ gap: 0 })`
  position: relative;
  isolation: isolate;
`;
const CloseButtonWrapper = styled.div`
  position: absolute;
  top: var(--unit);
  right: var(--unit);
  z-index: 1;
`;
const TimeLeftBanner = styled(Horizontal).attrs({ center: true, gap: 0.5 })`
  background: linear-gradient(
    101deg,
    #000040 -0.61%,
    #0047ff 45.52%,
    #fc00fc 96.07%
  );
  padding: var(--unit);
`;
const ContentWrapper = styled(Vertical).attrs({ center: true, gap: 3 })`
  padding: var(--triple-unit) var(--double-unit);
`;
const UppercaseLabelS = styled(LabelS)`
  text-transform: uppercase;
`;
const MarketCreditImg = styled.img`
  width: calc(9 * var(--unit));
  height: calc(9 * var(--unit));
  object-fit: contain;
`;
const TimelineWrapper = styled(Vertical).attrs({ gap: 0 })`
  --dot-width: 40px;
  align-self: flex-start;
`;
const TimelineRowWrapper = styled(Horizontal)`
  align-items: flex-start;
  position: relative;

  &:not(:last-child) {
    min-height: 64px;
  }
`;
const VerticalLine = styled.div`
  --line-width: 10px;
  position: absolute;
  width: var(--line-width);
  top: calc(var(--dot-width) - 1px);
  left: calc(var(--dot-width) / 2 - var(--line-width) / 2);
  bottom: -1px;
  background: linear-gradient(
    var(--c-white) 0%,
    var(--c-white) 50%,
    var(--c-nd-150) 51%,
    var(--c-nd-150) 10%
  );
`;
const RowContent = styled.div`
  padding-top: var(--unit);
`;
const Dot = styled(Horizontal).attrs({ gap: 0, center: true })`
  min-width: var(--dot-width);
  min-height: var(--dot-width);
  border-radius: 50%;
  box-sizing: border-box;
  z-index: 1;
  background-color: var(--c-white);
  color: var(--c-brand-600);

  &.notCompleted {
    color: var(--c-white);
    border: 4px solid var(--c-nd-150);
    background: var(--c-nd-50);
  }
`;
const Terms = styled(Caption)`
  text-decoration: underline;
  color: inherit;
`;
const FooterWrapper = styled(Vertical).attrs({ gap: 2 })`
  align-self: stretch;
`;

type Props = {
  open: boolean;
  onClose: () => void;
  onboardingPromotionalEvent: OnboardingPromoRecapDialog_so5OnboardingPromotionalEvent;
};
export const OnboardingPromoRecapDialog = ({
  open,
  onClose,
  onboardingPromotionalEvent,
}: Props) => {
  const { myPromotionalEvent } = onboardingPromotionalEvent;
  const { formatMessage } = useIntl();
  const trackOnce = useFootballEventOnce();

  const { endDate, rewardsProbabilisticBundleConfig } =
    myPromotionalEvent || {};
  const bundleConfig = rewardsProbabilisticBundleConfig?.at(0);
  const { conversionCredit } =
    bundleConfig?.slots.at(0)?.probableConfigs.at(0) || {};
  const { percentageDiscount } = conversionCredit || {};
  const discount = conversionCredit?.maxDiscount;

  const { main: discountAmount } = useAmountWithConversion({
    monetaryAmount: discount || zeroMonetaryAmount,
  });

  if (open) {
    trackOnce('View Onboarding Promo Dialog');
  }

  return (
    <Dialog open={open} fullWidth maxWidth="xs">
      {({ CloseButton }) => (
        <Root>
          <CloseButtonWrapper>
            <CloseButton onClose={onClose} />
          </CloseButtonWrapper>
          <img src={banner} alt="" />
          <TimeLeftBanner>
            <Clock width={12} height={12} />
            {endDate && (
              <LabelS bold>{formatTimeLeft(endDate, formatMessage)}</LabelS>
            )}
          </TimeLeftBanner>
          <ContentWrapper>
            <HeadlineM className="text-center">
              <FormattedMessage
                id="OnboardingPromoRecapDialog.title"
                defaultMessage="Compete for a chance to win {amount}"
                values={{
                  amount: (
                    <FormattedNumber
                      style="currency"
                      value={20000}
                      currency="USD"
                      maximumFractionDigits={0}
                    />
                  ),
                }}
              />
            </HeadlineM>
            <Horizontal gap={1.5}>
              <MarketCreditImg src={marketCredit} alt="" />
              <Vertical>
                <UppercaseLabelS bold color="var(--c-rivals-win)">
                  <FormattedMessage
                    id="OnboardingPromoRecapDialog.welcomeGift"
                    defaultMessage="Welcome Gift"
                  />
                </UppercaseLabelS>
                <BodyM>
                  <FormattedMessage
                    id="OnboardingPromoRecapDialog.promo2"
                    defaultMessage="Complete all the steps below to earn {discountPercentage, number, percent} off* your first Limited Card."
                    values={{ discountPercentage: percentageDiscount }}
                  />
                </BodyM>
              </Vertical>
            </Horizontal>
            <TimelineWrapper>
              <TimelineRowWrapper>
                <Dot>
                  <FontAwesomeIcon icon={faCheck} />
                </Dot>
                <VerticalLine />
                <RowContent>
                  <LabelL bold color="var(--c-nd-500)">
                    <FormattedMessage {...glossary.signup} />
                  </LabelL>
                </RowContent>
              </TimelineRowWrapper>
              <TimelineRowWrapper>
                <Dot className="notCompleted">
                  <LabelL bold>
                    <FormattedNumber value={2} />
                  </LabelL>
                </Dot>
                <RowContent>
                  <Vertical gap={2}>
                    <LabelL bold>
                      <FormattedMessage {...firstMissionDescription} />
                    </LabelL>
                  </Vertical>
                </RowContent>
              </TimelineRowWrapper>
            </TimelineWrapper>
            <FooterWrapper>
              <Button color="primary" size="medium" onClick={onClose}>
                <FormattedMessage
                  id="OnboardingPromoRecapDialog.selectPlayers"
                  defaultMessage="Select Players"
                />
              </Button>
              <SBHorizontal>
                <BodyS>
                  <FormattedMessage
                    id="OnboardingPromoRecapDialog.footnote"
                    defaultMessage="*{discountPercentage, number, percent} off up to {discountAmount}"
                    values={{
                      discountAmount,
                      discountPercentage: percentageDiscount,
                    }}
                  />
                </BodyS>
                <Terms
                  rel="noopener noreferrer"
                  target="_blank"
                  href="https://sorare.com/blog/credit-and-voucher-terms-and-conditions"
                  as="a"
                  color="var(--c-nd-700)"
                >
                  <FormattedMessage {...glossary.termsApply} />
                </Terms>
              </SBHorizontal>
            </FooterWrapper>
          </ContentWrapper>
        </Root>
      )}
    </Dialog>
  );
};

OnboardingPromoRecapDialog.fragments = {
  so5OnboardingPromotionalEvent: gql`
    fragment OnboardingPromoRecapDialog_so5OnboardingPromotionalEvent on So5OnboardingPromotionalEvent {
      id
      myPromotionalEvent {
        slug
        endDate
        rewardsProbabilisticBundleConfig {
          id
          slots {
            probableConfigs {
              conversionCredit {
                id
                percentageDiscount
                maxDiscount {
                  ...MonetaryAmountFragment_monetaryAmount
                }
              }
            }
          }
        }
      }
    }
    ${monetaryAmountFragment}
  ` as TypedDocumentNode<OnboardingPromoRecapDialog_so5OnboardingPromotionalEvent>,
};
